// The marker a destination gets on both maps.
//
// It is built as a DOM node rather than described twice, because MapLibre and Google both take an
// element and the two had started to drift apart. A destination is not a truck, so it is drawn
// unlike one in three independent ways: a pin rather than a pill, anchored at its tip rather than
// centred on the coordinate, and gently glowing so the eye finds where a load is going without
// hunting for a colour.
const STYLE_ID = 'operations-map-pin';
const GLOW = 'operations-site-pin';
// The destination's own colour. Violet keeps it out of the operational green/amber/red entirely,
// and matches the badge a trip carries once it is on its way to a customer.
const SITE = '#7B3FB0';

const ensureStyle = () => {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  // The glow is on the pin itself, not on a ring behind it, so it cannot be mistaken for a geofence.
  // Motion is a preference the operating system already holds an answer to, so it is asked.
  style.textContent = `
    @keyframes ${GLOW}-pulse {
      0%, 100% { filter: drop-shadow(0 0 2px rgba(255,255,255,.95)) drop-shadow(0 0 4px var(--pin-glow)); }
      50% { filter: drop-shadow(0 0 2px rgba(255,255,255,.95)) drop-shadow(0 0 14px var(--pin-glow)) drop-shadow(0 0 7px var(--pin-glow)); }
    }
    .${GLOW} svg { animation: ${GLOW}-pulse 2.4s ease-in-out infinite; }
    @media (prefers-reduced-motion: reduce) {
      .${GLOW} svg { animation: none; filter: drop-shadow(0 0 2px rgba(255,255,255,.95)) drop-shadow(0 0 8px var(--pin-glow)); }
    }`;
  document.head.appendChild(style);
};

/**
 * A destination, named by whoever the load is actually going to.
 *
 * The checkpoint behind it is usually called something generic — "Customer site" is what the
 * seeded plant calls it — which tells a dispatcher nothing. Where the trip names its customer,
 * that name is what goes on the map, and the checkpoint's own name goes to the tooltip.
 */
export const sitePin = (site) => {
  ensureStyle();
  const colour = site.color && site.color !== '#64748b' ? site.color : SITE;
  const pin = document.createElement('div');
  pin.className = GLOW;
  pin.title = site.title || site.label;
  pin.setAttribute('aria-label', `${site.label}, destination`);
  pin.style.setProperty('--pin-glow', colour);
  Object.assign(pin.style, {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', pointerEvents: 'none',
  });

  const name = document.createElement('span');
  name.textContent = site.label;
  Object.assign(name.style, {
    color: colour,
    font: '600 10px Roboto, sans-serif',
    background: 'rgba(255,255,255,.82)',
    borderRadius: '5px',
    padding: '1px 4px',
    whiteSpace: 'nowrap',
    maxWidth: '124px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  });

  const glyph = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  glyph.setAttribute('viewBox', '0 0 24 22');
  glyph.setAttribute('width', '15');
  glyph.setAttribute('height', '15');
  glyph.setAttribute('aria-hidden', 'true');
  const body = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  body.setAttribute('d', 'M12 0C7.58 0 4 3.58 4 8c0 6 8 14 8 14s8-8 8-14c0-4.42-3.58-8-8-8z');
  body.setAttribute('fill', colour);
  body.setAttribute('stroke', 'white');
  body.setAttribute('stroke-width', '1.5');
  const eye = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
  eye.setAttribute('cx', '12'); eye.setAttribute('cy', '8'); eye.setAttribute('r', '3');
  eye.setAttribute('fill', 'white');
  glyph.append(body, eye);

  pin.append(name, glyph);
  return pin;
};

export default sitePin;

// A truck on the map, as a truck.
//
// The marker used to be a pill carrying the number plate, which at yard density covered the road
// under it, the trail behind it and the lorry parked beside it. An icon says "vehicle" in a
// fraction of the ink; the plate is on the tooltip, and comes back as a label on whichever truck
// has been picked, because that one is the answer to a question somebody just asked.
const TRUCK = 'M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34'
  + ' 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96'
  + ' 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z';

/** The marker for one vehicle: a badge with a truck in it, and its plate when it is the chosen one. */
export const vehiclePin = (point, chosen) => {
  const button = document.createElement('button');
  button.type = 'button';
  button.title = point.label;
  button.setAttribute('aria-label', `Locate ${point.label}`);
  Object.assign(button.style, {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: chosen ? '3px 8px 3px 3px' : '3px',
    background: chosen ? '#0069ed' : point.color || '#123247',
    border: '1.5px solid white',
    borderRadius: '999px',
    cursor: 'pointer',
    boxShadow: chosen ? '0 3px 10px #0006' : '0 1px 5px #0004',
    lineHeight: 1,
  });

  const glyph = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  glyph.setAttribute('viewBox', '0 0 24 24');
  glyph.setAttribute('width', chosen ? '17' : '15');
  glyph.setAttribute('height', chosen ? '17' : '15');
  glyph.setAttribute('aria-hidden', 'true');
  glyph.style.display = 'block';
  const body = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  body.setAttribute('d', TRUCK);
  body.setAttribute('fill', '#fff');
  glyph.appendChild(body);
  button.appendChild(glyph);

  if (chosen) {
    const plate = document.createElement('span');
    plate.textContent = point.label;
    Object.assign(plate.style, { color: '#fff', font: '600 11px Roboto, sans-serif', whiteSpace: 'nowrap' });
    button.appendChild(plate);
  }
  return button;
};
